import React, { useEffect, useState } from 'react';
import { confirmAction } from '../utils/confirm';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchAllUsers, createUser, updateUser, deleteUser, UserRecord } from '../services/usersApi';
import { fetchAlertContacts, deleteAlertContact } from '../services/api';
import { getAllTimezones } from '../utils/timezone';
import PageHeader from './shared/PageHeader';
import { ui } from '../styles/shared';

const TZ_OPTIONS = getAllTimezones();

type UserRow = UserRecord;

// Stilar från den gemensamma stilfilen
const inp = ui.input;
const lbl = ui.label;
const btn = ui.btn;

const blankForm = () => ({
    username: '', email: '',
    userLevel: 'read' as 'admin' | 'read',
    timezone: 'Europe/Stockholm',
    enabled: true,
});

const Users: React.FC = () => {
    const { user: me, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState<UserRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [form, setForm] = useState(blankForm());
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            setUsers(await fetchAllUsers());
        } catch (e: any) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isAdmin) { navigate('/'); return; }
        load();
    }, [isAdmin, navigate]);

    const openCreate = () => { setEditId(null); setForm(blankForm()); setShowForm(true); };
    const openEdit = (u: UserRow) => {
        setEditId(u.id);
        setForm({
            username: u.username, email: u.email || '',
            userLevel: u.userLevel, timezone: u.timezone || 'Europe/Stockholm',
            enabled: u.enabled !== false,
        });
        setShowForm(true);
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            if (editId !== null) {
                    const payload: any = {
                        username: form.username, email: form.email,
                        userLevel: form.userLevel, timezone: form.timezone,
                        enabled: form.enabled,
                    };
                await updateUser(editId, payload);
                setSuccess('Användaren är sparad');
            } else {
                await createUser({ ...form });
                setSuccess('Användaren är skapad');
            }
            setShowForm(false);
            await load();
        } catch (e: any) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (u: UserRow) => {
        const { id, username } = u;
        if (!confirmAction(`Ta bort användaren "${username}"?`)) return;
        try {
            await deleteUser(id);
        } catch (e: any) {
            setError(e?.response?.data?.message || e.message);
            return;
        }
        // Användare och kontakter hänger inte ihop: en borttagen användare får annars
        // fortfarande larm och påminnelser (även push i appen) via sin kontakt.
        const removedContacts = await offerContactRemoval(u.email);
        setSuccess(`Användaren "${username}" är borttagen` +
            (removedContacts.length ? `, liksom kontakten ${removedContacts.map(n => `"${n}"`).join(', ')}` : ''));
        await load();
    };

    /** Frågar om kontakter med samma e-post ska tas bort. Svarar med namnen på de borttagna. */
    const offerContactRemoval = async (email: string | null): Promise<string[]> => {
        const wanted = email?.trim().toLowerCase();
        if (!wanted) return [];
        let matches: { id: number; name: string }[] = [];
        try {
            const contacts = await fetchAlertContacts();
            matches = contacts.filter(c => c.email?.trim().toLowerCase() === wanted);
        } catch { return []; }
        const removed: string[] = [];
        for (const c of matches) {
            if (!confirmAction(
                `Kontakten "${c.name}" har samma e-post och får fortfarande larm och påminnelser, även som push i appen.

` +
                `Ta bort kontakten också? Scheman och mallar som pekar på den tappar sin mottagare.`
            )) continue;
            try {
                await deleteAlertContact(c.id);
                removed.push(c.name);
            } catch (e: any) {
                setError(`Kunde inte ta bort kontakten "${c.name}": ${e.message}`);
            }
        }
        return removed;
    };

    const toggleEnabled = async (u: UserRow) => {
        const next = !(u.enabled === false ? false : true);
        const verb = next ? 'Slå på' : 'Stäng av';
        if (!confirmAction(`${verb} kontot för "${u.username}"?`)) return;
        try {
            await updateUser(u.id, { enabled: next });
            setSuccess(`Kontot för "${u.username}" är ${next ? 'påslaget' : 'avstängt'}`);
            await load();
        } catch (e: any) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    return (
        <div>
            <PageHeader icon="👥" title="Användare" description="Konton som kan logga in. Admin får ändra inställningar, övriga kan se och göra kontroller." />


            {error && (
                <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '12px 16px', borderRadius: '4px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{error}</span>
                    <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', color: '#721c24' }}>✕</button>
                </div>
            )}
            {success && (
                <div style={{ backgroundColor: '#d4edda', color: '#155724', padding: '12px 16px', borderRadius: '4px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{success}</span>
                    <button onClick={() => setSuccess(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', color: '#155724' }}>✕</button>
                </div>
            )}

            {/* Toolbar */}
            <div style={{ marginBottom: '16px' }}>
                <button onClick={openCreate} style={btn('#28a745')}>+ Ny användare</button>
            </div>

            {/* Create / Edit form */}
            {showForm && (
                <div style={{ backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                    <h3 style={{ marginTop: 0 }}>{editId ? 'Ändra användare' : 'Ny användare'}</h3>
                    <form onSubmit={submit}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                            <div>
                                <label style={lbl}>Användarnamn *</label>
                                <input style={inp} required value={form.username}
                                    onChange={e => setForm(p => ({ ...p, username: e.target.value }))} />
                            </div>
                            <div>
                                <label style={lbl}>E-post</label>
                                <input style={inp} type="email" value={form.email}
                                    placeholder="hit skickas inloggningskoden"
                                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
                            </div>
                            <div>
                                <label style={lbl}>Behörighet</label>
                                <select style={inp} value={form.userLevel}
                                    onChange={e => setForm(p => ({ ...p, userLevel: e.target.value as any }))}>
                                    <option value="read">läsa</option>
                                    <option value="admin">admin</option>
                                </select>
                            </div>
                            <div>
                                <label style={lbl}>Tidszon</label>
                                <select style={inp} value={form.timezone}
                                    onChange={e => setForm(p => ({ ...p, timezone: e.target.value }))}>
                                    {TZ_OPTIONS.map(tz => (
                                        <option key={tz} value={tz}>{tz}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label style={lbl}>Konto</label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={form.enabled}
                                        disabled={editId === me?.id}
                                        onChange={e => setForm(p => ({ ...p, enabled: e.target.checked }))} />
                                    {form.enabled ? 'Påslaget' : 'Avstängt (kan inte logga in)'}
                                </label>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="submit" disabled={saving} style={btn('#007bff')}>{saving ? 'Sparar…' : editId ? 'Spara' : 'Skapa användare'}</button>
                            <button type="button" onClick={() => setShowForm(false)} style={btn('#6c757d')}>Avbryt</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Users table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Hämtar…</div>
            ) : (
                <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #ddd', overflow: 'hidden' }}>
                    <div className="tbl-scroll">
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                {['Användarnamn', 'E-post', 'Behörighet', 'Läge', 'Tidszon', 'Skapad', 'Åtgärder'].map(h => (
                                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 'bold', color: '#555', borderBottom: '1px solid #ddd' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 ? (
                                <tr><td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Inga användare</td></tr>
                            ) : users.map((u, i) => (
                                <tr key={u.id} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#fafafa', borderBottom: '1px solid #f0f0f0', opacity: u.enabled === false ? 0.55 : 1 }}>
                                    <td style={{ padding: '10px 16px', fontWeight: 'bold' }}>
                                        {u.username}
                                        {u.id === me?.id && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#888' }}>(du)</span>}
                                    </td>
                                    <td style={{ padding: '10px 16px', color: '#555', fontSize: '13px' }}>
                                        {u.email || <span style={{ color: '#bbb' }}>—</span>}
                                    </td>
                                    <td style={{ padding: '10px 16px' }}>
                                        <span style={{
                                            padding: '2px 8px', borderRadius: '10px', fontSize: '12px',
                                            backgroundColor: u.userLevel === 'admin' ? '#fff3cd' : '#e8f4fd',
                                            color: u.userLevel === 'admin' ? '#856404' : '#004085'
                                        }}>
                                            {u.userLevel === 'admin' ? 'admin' : 'läsa'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '10px 16px' }}>
                                        {u.enabled === false ? (
                                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '12px', backgroundColor: '#f8d7da', color: '#721c24' }}>avstängt</span>
                                        ) : (
                                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '12px', backgroundColor: '#d4edda', color: '#155724' }}>aktivt</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '10px 16px', color: '#555', fontSize: '13px' }}>
                                        {u.timezone || '—'}
                                    </td>
                                    <td style={{ padding: '10px 16px', color: '#666', fontSize: '13px' }}>
                                        {new Date(u.createdAt).toLocaleDateString('sv-SE')}
                                    </td>
                                    <td style={{ padding: '10px 16px' }}>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button onClick={() => openEdit(u)} style={btn('#ffc107', '#000')}>Ändra</button>
                                            {u.id !== me?.id && (
                                                <button
                                                    onClick={() => toggleEnabled(u)}
                                                    style={btn(u.enabled === false ? '#28a745' : '#6c757d')}
                                                    title={u.enabled === false ? 'Slå på kontot' : 'Stäng av kontot'}>
                                                    {u.enabled === false ? 'Slå på' : 'Stäng av'}
                                                </button>
                                            )}
                                            {u.id !== me?.id && (
                                                <button onClick={() => handleDelete(u)} style={btn('#dc3545')}>Ta bort</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;
